import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, TextField, Theme, Typography, alpha } from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import { IVacationAwardingCrew } from "@vmosolutions/oc-interfaces";

import { VACATION_BID_TEST_ID } from "../testIds";
import {
  QueueState,
  crewMatchesSearch,
  crewName,
  hiddenWhenDone,
  openWarnings,
  queueCounts,
  queueState,
} from "./awardingHelpers";

export interface IAwardQueueProps {
  /** Most senior first. */
  crew: IVacationAwardingCrew[];
  selectedUserId: string | null;
  onSelect: (userId: string) => void;
}

const STATE_LABEL: Record<QueueState, string> = {
  now: "Now",
  pending: "Pending",
  skipped: "Skipped",
  done: "Done",
};

const AwardQueueStyles = (theme: Theme): any => ({
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.custom.forms.inputBorder}`,
  borderRadius: "3px",
  "& .queue_header": {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 8px 6px 12px",
    "& .MuiButtonBase-root": { padding: "1px 6px", fontSize: 11, whiteSpace: "nowrap" },
  },
  "& .queue_title": {
    flex: 1,
    fontSize: 11,
    textTransform: "uppercase",
    color: theme.custom.forms.labels,
  },
  "& .queue_counts": { fontSize: 11, color: theme.custom.forms.labels, padding: "0 12px 6px" },
  "& .queue_search": {
    padding: "0 8px 8px",
    "& .MuiInputBase-root": { backgroundColor: theme.custom.forms.inputBack, color: theme.custom.forms.inputText },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.custom.forms.inputBorder },
    "& .MuiInputBase-input": { fontSize: 13, padding: "5px 8px" },
  },
  "& .queue_list": { flex: 1, minHeight: 0, overflowY: "auto" },
  "& .queue_empty": { fontSize: 13, color: theme.custom.forms.labels, padding: "10px 12px" },
  "& .queue_item": {
    // A button, reset to read as a list row.
    font: "inherit",
    color: "inherit",
    textAlign: "left",
    width: "100%",
    background: "none",
    border: 0,
    display: "grid",
    gridTemplateColumns: "34px minmax(0, 1fr) 18px auto",
    alignItems: "center",
    padding: "7px 12px",
    cursor: "pointer",
    borderTop: `1px solid ${theme.custom.forms.inputBorder}`,
    borderLeft: "3px solid transparent",
    "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: "-2px" },
    "&.now": {
      borderLeftColor: theme.palette.secondary.main,
      backgroundColor: alpha(theme.palette.secondary.main, 0.12),
      fontWeight: 700,
    },
    "& .queue_position": { color: theme.custom.forms.labels, fontSize: 12 },
    "& .queue_name": { fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    "& .queue_state": { fontSize: 10, textTransform: "uppercase", color: theme.custom.forms.labels },
    "& .queue_warning": { display: "flex", fontSize: 14, color: theme.palette.warning.light },
    "&.now .queue_state": { color: theme.palette.secondary.main },
    "&.done .queue_name": { color: theme.custom.forms.labels },
    "&.done .queue_state": { color: theme.palette.success.main },
    "&.skipped .queue_state": { color: theme.palette.warning.light },
  },
});

/**
 * The crew in seniority order, and where the scheduler has got to with each. Crew marked complete are
 * hidden until asked for, apart from the one selected and any with a warning still open.
 */
export const AwardQueue = (props: IAwardQueueProps): JSX.Element => {
  const { crew, selectedUserId, onSelect } = props;
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const [search, setSearch] = useState<string>("");
  const [hideDone, setHideDone] = useState<boolean>(true);

  const counts = useMemo(() => queueCounts(crew), [crew]);
  // Each keeps its place in the seniority order however many are filtered out.
  const shown = useMemo(
    () =>
      crew
        .map((member, index) => ({ member, position: index + 1 }))
        .filter(
          ({ member }) => !(hideDone && hiddenWhenDone(member, selectedUserId)) && crewMatchesSearch(member, search)
        ),
    [crew, hideDone, selectedUserId, search]
  );

  // The queue moves on by itself after Skip or Mark complete, so the new crew member is brought into view.
  useEffect(() => {
    if (selectedUserId) itemRefs.current.get(selectedUserId)?.scrollIntoView({ block: "nearest" });
  }, [selectedUserId]);

  return (
    <Box sx={AwardQueueStyles} data-testid={VACATION_BID_TEST_ID.awardQueue}>
      <div className="queue_header">
        <Typography className="queue_title">Award queue</Typography>
        <Button
          size="small"
          variant={hideDone ? "outlined" : "contained"}
          disabled={!counts.done}
          onClick={() => setHideDone((current) => !current)}
          data-testid={VACATION_BID_TEST_ID.awardQueueDoneToggle}
        >
          {hideDone ? `Show done (${counts.done})` : "Hide done"}
        </Button>
      </div>
      <Typography className="queue_counts" data-testid={VACATION_BID_TEST_ID.awardQueueCounts}>
        {`${counts.pending} pending · ${counts.skipped} skipped · ${counts.done} done`}
      </Typography>
      <div className="queue_search">
        <TextField
          fullWidth
          size="small"
          placeholder="Search name or employee id"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          // Enter jumps to the first match. The editor's form around this tab would otherwise take it as a submit.
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            if (shown.length) onSelect(shown[0].member.userId);
          }}
          inputProps={{ "data-testid": VACATION_BID_TEST_ID.awardQueueSearch }}
        />
      </div>
      <div className="queue_list">
        {!shown.length && (
          <Typography className="queue_empty">
            {search.trim() ? "No crew match the search." : "Everyone is marked complete."}
          </Typography>
        )}
        {shown.map(({ member, position }) => {
          const state = queueState(member, selectedUserId);
          return (
            <button
              type="button"
              key={member.userId}
              ref={(element) => {
                if (element) itemRefs.current.set(member.userId, element);
                else itemRefs.current.delete(member.userId);
              }}
              className={`queue_item ${state}`}
              aria-current={state === "now" ? "true" : undefined}
              onClick={() => onSelect(member.userId)}
              data-testid={VACATION_BID_TEST_ID.awardQueueItem}
            >
              <span className="queue_position">{position}</span>
              <span className="queue_name">{crewName(member)}</span>
              <span className="queue_warning">
                {openWarnings(member).length > 0 && (
                  <WarningIcon
                    fontSize="inherit"
                    titleAccess="An entitlement change warning is open"
                    data-testid={VACATION_BID_TEST_ID.awardQueueWarning}
                  />
                )}
              </span>
              <span className="queue_state">{STATE_LABEL[state]}</span>
            </button>
          );
        })}
      </div>
    </Box>
  );
};

export default AwardQueue;
